// Phase-A — Creator handshake page.
//
// When an existing creator/affiliate-creator clicks the "Affiliate Creator"
// pill on someone else's public Q&A (instead of the "Sign up" pill that anon
// visitors see), they land here. It's a celebratory acknowledgement that
// they're already on the inside — no upsell, no signup form, just a nod.
//
// Spec from user (Feb 2026):
//   "this link is going to lead to something cool. Could be a special
//    renowned page; for now let's just make it a jecstatic image."
//
// Phase B will turn this into the Silo Wars / Collaboration / Apprenticeship
// drawer entry point. For now: a striking, calm full-bleed page with a
// handshake illustration and a button back to the visitor's own silos.

import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Sparkle } from "@phosphor-icons/react";
import { useViewerSurface } from "@/lib/useViewerSurface";

export default function CreatorNod() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { isOperator, isAffiliate, user } = useViewerSurface();
  const fromName = params.get("from_name") || "another operator";
  const fromSilo = params.get("from_silo") || "";

  // Defensive: if a non-operator somehow lands here (deep link from a fan),
  // redirect them to /signup with the seed preserved. The handshake doesn't
  // belong to fans.
  if (!isOperator) {
    const ref = params.get("ref") || params.get("from") || "";
    const qs = ref ? `?ref=${encodeURIComponent(ref)}` : "";
    nav(`/signup${qs}`, { replace: true });
    return null;
  }

  return (
    <div
      className="min-h-screen w-full relative overflow-hidden"
      style={{
        background:
          "radial-gradient(circle at 30% 20%, #4A5D4E 0%, #2A3A2E 40%, #1C1B1A 100%)",
      }}
      data-testid="creator-nod-page"
    >
      {/* Grain texture overlay */}
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' /></filter><rect width='100%' height='100%' filter='url(%23n)' /></svg>\")",
        }}
      />

      {/* Soft glints */}
      <motion.div
        className="absolute top-1/4 left-1/4 w-2 h-2 rounded-full bg-[#C16E5A]"
        animate={{ opacity: [0.2, 0.9, 0.2], scale: [1, 1.4, 1] }}
        transition={{ duration: 4, repeat: Infinity, delay: 0.3 }}
      />
      <motion.div
        className="absolute top-1/3 right-1/3 w-1.5 h-1.5 rounded-full bg-[#FDFCF8]"
        animate={{ opacity: [0.1, 0.7, 0.1], scale: [1, 1.6, 1] }}
        transition={{ duration: 5, repeat: Infinity, delay: 1.1 }}
      />
      <motion.div
        className="absolute bottom-1/3 left-1/2 w-1 h-1 rounded-full bg-[#C16E5A]"
        animate={{ opacity: [0.15, 0.8, 0.15], scale: [1, 1.5, 1] }}
        transition={{ duration: 3.5, repeat: Infinity, delay: 2 }}
      />

      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Top bar — back link */}
        <header className="px-6 py-5">
          <button
            onClick={() => nav(-1)}
            data-testid="creator-nod-back"
            className="text-[#FDFCF8]/70 hover:text-[#FDFCF8] flex items-center gap-1.5 text-xs uppercase tracking-[0.2em]"
          >
            <ArrowLeft size={14} /> Back
          </button>
        </header>

        {/* Hero */}
        <main className="siloart-copy flex-1 flex items-center justify-center px-6 py-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="max-w-2xl w-full text-center"
          >
            {/* Handshake illustration — pure SVG, no asset dependency. Two
                interlocking arcs that read as a clasp/handshake at a glance. */}
            <motion.div
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, delay: 0.3, ease: "easeOut" }}
              className="mx-auto mb-10 w-40 h-40 sm:w-56 sm:h-56"
            >
              <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
                <defs>
                  <radialGradient id="ringL" cx="0.3" cy="0.3" r="0.8">
                    <stop offset="0%" stopColor="#C16E5A" stopOpacity="1" />
                    <stop offset="100%" stopColor="#C16E5A" stopOpacity="0.3" />
                  </radialGradient>
                  <radialGradient id="ringR" cx="0.7" cy="0.3" r="0.8">
                    <stop offset="0%" stopColor="#FDFCF8" stopOpacity="0.95" />
                    <stop offset="100%" stopColor="#FDFCF8" stopOpacity="0.3" />
                  </radialGradient>
                </defs>
                <circle cx="80" cy="100" r="48" stroke="url(#ringL)" strokeWidth="3" fill="none" />
                <circle cx="120" cy="100" r="48" stroke="url(#ringR)" strokeWidth="3" fill="none" />
                {/* center clasp */}
                <circle cx="100" cy="100" r="6" fill="#C16E5A" />
                <circle cx="100" cy="100" r="14" stroke="#FDFCF8" strokeWidth="0.5" strokeOpacity="0.5" fill="none" />
              </svg>
            </motion.div>

            <div className="text-[10px] uppercase tracking-[0.4em] text-[#C16E5A] mb-4 flex items-center justify-center gap-2">
              <Sparkle size={12} weight="duotone" /> Operator handshake <Sparkle size={12} weight="duotone" />
            </div>

            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#FDFCF8] leading-tight">
              Two operators.<br />
              <em className="text-[#C16E5A]">One silo network.</em>
            </h1>

            <p className="text-base sm:text-lg text-[#E5E5E0]/80 mt-6 max-w-lg mx-auto leading-relaxed">
              You and {fromName === "another operator" ? <span>{fromName}</span> : <strong className="text-[#FDFCF8]">{fromName}</strong>} are both building here. No sign-up nag, no upsell — just a nod.
              {isAffiliate
                ? " You're already inside the Creator Pro tier; the tools you'll need are already yours."
                : " Welcome to the inside."}
            </p>

            {/* Sprint Feb 8, 2026 — the inline "Silo Wars · Collaboration ·
                Apprenticeship — soon" teaser pill was removed. That role is
                now handled by the persistent Silo Actions Drawer that
                appears in the upper-right whenever you visit another
                operator's silo. CreatorNod stays focused on the handshake
                moment without redundant chrome. */}

            {/* CTAs */}
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/dashboard"
                data-testid="creator-nod-back-to-silos"
                className="px-6 py-3 rounded-md bg-[#C16E5A] text-white hover:bg-[#A85E4A] flex items-center gap-2 text-sm font-medium transition-colors"
              >
                Back to your silos <ArrowRight size={16} />
              </Link>
              {fromSilo && (
                <Link
                  to={`/s/${fromSilo}`}
                  data-testid="creator-nod-keep-reading"
                  className="px-6 py-3 rounded-md border border-[#FDFCF8]/25 text-[#FDFCF8] hover:bg-[#FDFCF8]/5 flex items-center gap-2 text-sm transition-colors"
                >
                  Keep reading {fromName === "another operator" ? "their silo" : `${fromName}'s silo`}
                </Link>
              )}
            </div>

            {user?.name && (
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#FDFCF8]/40 mt-12">
                Signed in as {user.name}
              </p>
            )}
          </motion.div>
        </main>
      </div>
    </div>
  );
}
